/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  PUSH OPT-IN NUDGE — cadence d'affichage (localStorage, par appareil)
 * ─────────────────────────────────────────────────────────────────────────────
 *  Objectif : inviter à activer les notifications push un clic (CTA vert),
 *  de façon récurrente mais respectueuse, au lieu de la seule activation
 *  manuelle dans Paramètres → Notifications.
 *
 *  Règles de cadence (design anti-harcèlement, par appareil/navigateur) :
 *   - Première impression          : après ~8 s, si le push est possible
 *   - Bouton "Plus tard"           : re-apparaît dans +48 h
 *   - Fermeture (X / swipe)        : re-apparaît dans +7 jours
 *   - 3 impressions atteintes      : pause de +14 jours
 *   - Refus navigateur (permission) : pause courte, puis +30 j après 2 refus
 *   - Utilisateur déjà abonné      : plus jamais affiché (opt-in définitif)
 *
 *  L'état est stocké en localStorage par appareil : chaque navigateur de
 *  l'utilisateur fait son propre cycle (cohérent avec `pushManager`, qui est
 *  lui-même par orbite du navigateur).
 * ─────────────────────────────────────────────────────────────────────────────
 */

import {
  getSoftPromptBlockReason,
  recordSoftPromptAccepted,
  recordSoftPromptClosed,
  recordSoftPromptLater,
  recordSoftPromptShown,
  seedSoftPrompt,
} from "./soft-prompt";

import type { SoftPromptBlockReason, SoftPromptOptions } from "./soft-prompt";

const STORAGE_KEY = "push_optin_nudge_v1";

// Intervalles (ms)
const LATER_COOLDOWN = 48 * 60 * 60 * 1000; // +48 h
const CLOSE_COOLDOWN = 7 * 24 * 60 * 60 * 1000; // +7 j
const MAX_IMPRESSIONS = 3;
const EXHAUSTED_COOLDOWN = 14 * 24 * 60 * 60 * 1000; // +14 j
const DENIED_ALLOWANCE = 2;
const DENIED_COOLDOWN = 30 * 24 * 60 * 60 * 1000; // +30 j

/** Identifiant de ce prompt dans le registre SoftPrompt partagé (#115). */
export const PUSH_PROMPT_ID = "push_optin";

/** Cadence déléguée au socle — mêmes valeurs que l'implémentation historique. */
const SOFT_PROMPT_OPTIONS: SoftPromptOptions = {
  laterCooldownMs: LATER_COOLDOWN,
  closeCooldownMs: CLOSE_COOLDOWN,
  maxImpressions: MAX_IMPRESSIONS,
  exhaustedCooldownMs: EXHAUSTED_COOLDOWN,
  reactivateOn: ["cv_completed"],
};

/**
 * État push-specific (reste ici : le socle ne connaît ni le refus navigateur
 * ni l'opt-in définitif). La cadence (impressions / Plus tard / fermeture)
 * vit désormais dans le registre SoftPrompt sous `PUSH_PROMPT_ID`.
 */
type NudgeState = {
  deniedUntil: number | null;
  deniedCount: number;
  optedIn: boolean;
};

/** Ancien format (#116) : cadence locale, impressions comptées à l'armement. */
type LegacyNudgeState = NudgeState & {
  impressions?: number;
  lastShownAt?: number | null;
  laterUntil?: number | null;
  closeUntil?: number | null;
};

const DEFAULT_STATE: NudgeState = {
  deniedUntil: null,
  deniedCount: 0,
  optedIn: false,
};

const load = (): NudgeState => {
  if (typeof window === "undefined") return { ...DEFAULT_STATE };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_STATE };
    const parsed = JSON.parse(raw) as Partial<LegacyNudgeState>;
    if (!parsed || typeof parsed !== "object") return { ...DEFAULT_STATE };

    // ── Migration (#116, idempotente) ─────────────────────────────────
    if (typeof parsed.impressions === "number") {
      // 1. Cadence → socle SoftPrompt. RESET des impressions : l'ancienne
      //    version les comptait à l'armement (impressions fantômes) — un
      //    simple rechargement consommait un quota et pouvait enclencher la
      //    pause 14 j silencieuse. Les clics réels (Plus tard / fermeture)
      //    sont conservés.
      seedSoftPrompt(PUSH_PROMPT_ID, {
        impressions: 0,
        lastShownAt: null,
        laterUntil: parsed.laterUntil ?? null,
        closeUntil: parsed.closeUntil ?? null,
      });
      // 2. État push-specific conservé (refus navigateur, opt-in).
      const migrated: NudgeState = {
        deniedCount: parsed.deniedCount ?? 0,
        deniedUntil: parsed.deniedUntil ?? null,
        optedIn: Boolean(parsed.optedIn),
      };
      save(migrated);
      return migrated;
    }

    return { ...DEFAULT_STATE, ...parsed };
  } catch {
    return { ...DEFAULT_STATE };
  }
};

const save = (state: NudgeState) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // localStorage indisponible (mode privé en bloc, quota…) → on ignore.
  }
};

/**
 * Raison pour laquelle le toast push est bloqué (#116 : diagnostic —
 * aucune pause, y compris la pause 14 j, n'a le droit d'être silencieuse).
 * `denied` = refus navigateur ; le reste vient du socle SoftPrompt.
 */
export type PushNudgeBlockReason =
  | SoftPromptBlockReason
  | "denied"
  | "opted-in";

export const getPushNudgeBlockReason = (): PushNudgeBlockReason | null => {
  const s = load();
  if (s.optedIn) return "opted-in";

  const now = Date.now();

  // Refus navigateur : pause courte (7 j) après 1 refus, pause longue
  // (+30 j) après 2 refus. `recordPushNudgeDenied` écrit déjà l'échéance
  // dans `deniedUntil` — on la lit directement (l'ancien code ajoutait un
  // second `DENIED_COOLDOWN`, soit 60 j réels contre 30 j documentés).
  if (now < (s.deniedUntil ?? 0)) return "denied";

  // Cadence (impressions / Plus tard / fermeture) → socle SoftPrompt (#115).
  return getSoftPromptBlockReason(PUSH_PROMPT_ID, SOFT_PROMPT_OPTIONS);
};

/** Retourne true si le nudge doit être proposé maintenant. */
export const shouldShowPushNudge = (): boolean =>
  getPushNudgeBlockReason() === null;

/**
 * Impression — à appeler UNIQUEMENT au moment réel de l'affichage (dans le
 * callback du setTimeout), jamais à l'armement : c'est le bug d'impression
 * fantôme corrigé par #116.
 */
export const recordPushNudgeShown = () =>
  recordSoftPromptShown(PUSH_PROMPT_ID);

export const recordPushNudgeLater = () =>
  recordSoftPromptLater(PUSH_PROMPT_ID, SOFT_PROMPT_OPTIONS);

export const recordPushNudgeClosed = () =>
  recordSoftPromptClosed(PUSH_PROMPT_ID, SOFT_PROMPT_OPTIONS);

export const recordPushNudgeDenied = () => {
  const s = load();
  s.deniedCount += 1;
  s.deniedUntil =
    Date.now() + (s.deniedCount >= DENIED_ALLOWANCE ? DENIED_COOLDOWN : CLOSE_COOLDOWN);
  save(s);
};

export const recordPushOptedIn = () => {
  save({ ...load(), optedIn: true });
  // Opt-in = acceptation définitive : le socle ne repropose plus jamais.
  recordSoftPromptAccepted(PUSH_PROMPT_ID);
};

export const isPushNudgeOptedIn = (): boolean => load().optedIn;
